import type { StudentTestContent } from "p6m-p6u";
import { useRTContext } from "../context/RTContext";
import { useTestCreationContext } from "../context/TestCreationContext";
import { useLogger } from "./useLogger";

export interface ReassignData {
    sharedContent: StudentTestContent | null;
}

export const useSchoolClassOperations = () => {
    const { updateVocabTrainingSchoolClass, fetchAndStoreVocabTrainings } = useRTContext();
    const { updateTestSchoolClass, reassignSharedTestToClass } = useTestCreationContext();
    const { logEvent } = useLogger();

    // Both handlers honor the SchoolClassChangeHandler contract: resolve whether the write landed, never
    // reject — false triggers a cascading class-delete rollback in the assignment popup, so nothing after
    // the successful write may throw or return false.
    const handleTestSchoolClassChange = async (
        testId: string,
        schoolClassId: string | null,
        reassignData?: ReassignData
    ): Promise<boolean> => {
        if (reassignData) {
            const newTestId = await reassignSharedTestToClass({
                testId,
                newSchoolClassId: schoolClassId,
                sharedContent: reassignData.sharedContent,
            });
            return newTestId !== null;
        }

        // The context rethrows after showing its warning modal; map that onto the boolean contract here.
        const isAssigned = await updateTestSchoolClass(testId, schoolClassId).catch((e) => {
            console.error("Failed to assign the class to the test", e);
            return false;
        });
        if (!isAssigned) return false;

        // `classId` is the established Amplitude property key and stays as-is.
        logEvent("Test School Class Changed", {
            testId,
            classId: schoolClassId ?? "removed",
        });
        return true;
    };

    const handleVocabTrainingSchoolClassChange = async (
        trainingId: string,
        schoolClassId: string | null
    ): Promise<boolean> => {
        // A failed write leaves the training on its old class and has already shown its own warning,
        // so there is nothing new to fetch and nothing to report as a class change.
        const isAssigned = await updateVocabTrainingSchoolClass(trainingId, schoolClassId);
        if (!isAssigned) return false;

        // The write has landed — a failed refresh only leaves the list stale and must not trip the
        // rollback (contract above), so any rejection stops here.
        await fetchAndStoreVocabTrainings().catch((e) => {
            console.warn("Failed to refresh vocab trainings after the class change", e);
        });
        logEvent("Vocab Training School Class Changed", {
            trainingId,
            classId: schoolClassId ?? "removed",
        });
        return true;
    };

    return {
        handleTestSchoolClassChange,
        handleVocabTrainingSchoolClassChange,
    };
};
